import React, { useState, useEffect } from "react";
import { Form, Input, Button, message } from "antd";
import { Link } from "react-router-dom";
import logo from "../assets/logo.png";
import { API_BASE_URL } from "../api/api";

const LoginForm = ({ onLogin, loading: externalLoading }) => {
  const [form] = Form.useForm();
  const [step, setStep] = useState("login"); // "login" | "verify"
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [password, setPassword] = useState("");

  // Countdown effect
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      if (step === "login") {
        const res = await fetch(`${API_BASE_URL}auth_login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(values),
        });
        const data = await res.json();

        if (res.ok && data.step === "verify") {
          setEmail(values.email);
          setPassword(values.password); // <- Save password here
          setStep("verify");
          setCountdown(60);
          message.success(data.message || "Verification code sent");
          form.resetFields(["password"]);
        } else {
          message.error(data.error || "Login failed");
        }
      } else if (step === "verify") {
        const res = await fetch(`${API_BASE_URL}verify_login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, code: values.code }),
        });

        const data = await res.json();
        if (res.ok && data.token) {
          onLogin({ token: data.token, user: data.user });
          message.success("Login successful");
        } else {
          message.error(data.error || "Verification failed");
        }
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}auth_login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }), // <-- include saved password
      });
      const data = await res.json();
      if (res.ok && data.step === "verify") {
        message.success("Verification code resent");
        setCountdown(60);
      } else {
        message.error(data.error || "Failed to resend code");
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(135deg, #eef2f3, #8e9eab)",
        padding: 20,
      }}
    >
      <div
        style={{
          width: 420,
          padding: "48px 36px",
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 14px 40px rgba(0,0,0,0.1)",
          textAlign: "center",
        }}
      >
        <img
          src={logo}
          alt="CRM Logo"
          style={{ width: 90, marginBottom: 20 }}
        />
        <h2 style={{ marginBottom: 6, fontWeight: 600, fontSize: 24 }}>
          CRM Portal Login
        </h2>
        <p style={{ marginBottom: 28, color: "#666" }}>
          {step === "login"
            ? "Manage customers, orders, and more"
            : `A verification code was sent to ${email}`}
        </p>

        <Form layout="vertical" form={form} onFinish={handleLogin}>
          <Form.Item
            label="Email Address"
            name="email"
            rules={[{ required: true, message: "Please enter your email" }]}
            initialValue={email}
          >
            <Input
              placeholder="example@company.com"
              disabled={step === "verify"}
            />
          </Form.Item>

          {step === "login" && (
            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: "Please enter your password" },
              ]}
            >
              <Input.Password placeholder="Your secure password" />
            </Form.Item>
          )}

          {step === "verify" && (
            <>
              <Form.Item
                label="Verification Code"
                name="code"
                rules={[
                  { required: true, message: "Please enter the 6-digit code" },
                ]}
              >
                <Input placeholder="Enter 6-digit code" maxLength={6} />
              </Form.Item>

              <div style={{ marginBottom: 16 }}>
                {countdown > 0 ? (
                  <span style={{ fontSize: 13, color: "#999" }}>
                    Resend code in {countdown}s
                  </span>
                ) : (
                  <Button type="link" onClick={handleResend}>
                    Resend Code
                  </Button>
                )}
              </div>
            </>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading || externalLoading}
              style={{ marginTop: 12 }}
            >
              {step === "login" ? "Access CRM" : "Verify Code"}
            </Button>
          </Form.Item>
        </Form>

        {step === "login" && (
          <div style={{ marginTop: 16 }}>
            <Link to="/forgot-password" style={{ fontSize: 12, color: "#888" }}>
              Forgot password?
            </Link>
          </div>
        )}

        <div style={{ fontSize: 12, color: "#aaa", marginTop: 30 }}>
          © {new Date().getFullYear()} Sunbook.ca · All rights reserved
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
